'use client';

import { createClient } from '@/lib/supabase/client';

// ─── TYPES ───────────────────────────────────────────────────

export interface HmrcSettings {
  id?: string;
  companyId: string;
  payeReference: string;
  accountsOfficeReference: string;
  corporationTaxReference: string;
  vatNumber: string;
  taxYear: string;
  paymentFrequency: string;
  niCategory: string;
  hmrcGatewayUsername: string;
  lastRtiSubmission: string | null;
  nextPaymentDue: string | null;
  rtiEnabled: boolean;
  autoEnrolmentEnabled: boolean;
  pensionStagingDate: string | null;
}

export interface CompanyDefaults {
  id?: string;
  companyId: string;
  companyName: string;
  companiesHouseNumber: string;
  registeredAddress: string;
  tradingAddress: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  currency: string;
  timezone: string;
  dateFormat: string;
  language: string;
  fiscalYearStart: string;
}

export interface ShiftPayrollRules {
  id?: string;
  companyId: string;
  standardHoursWeek: number;
  otThresholdDaily: number;
  otThresholdWeekly: number;
  otMultiplier: number;
  afternoonShiftStart: string;
  afternoonShiftEnd: string;
  afternoonPremium: number;
  nightShiftStart: string;
  nightShiftEnd: string;
  nightPremium: number;
  weekendPremium: number;
  bankHolidayMultiplier: number;
  minRestBetweenShifts: number;
  maxConsecutiveDays: number;
  breakDurationMinutes: number;
  breakAfterHours: number;
  holidayEntitlementDays: number;
  sickPayDays: number;
  payrollCutoffDay: number;
  paymentDay: number;
  timeRoundingRule: string;
  weeklyPayrollCutoffDay: string;
  weeklyPaymentDay: string;
}

export interface TenantLocation {
  id?: string;
  companyId: string;
  name: string;
  code: string;
  address: string;
  city: string;
  postcode: string;
  region: string;
  managerName: string;
  managerEmail: string;
  costCentre: string;
  operatingHours: string;
  capacity: number;
  currentHeadcount: number;
  status: string;
  locationType: string;
  geofenceLat: number | null;
  geofenceLng: number | null;
  geofenceRadius: number;
}

export interface SystemPolicies {
  id?: string;
  companyId: string;
  twoFactorAuthEnabled: boolean;
  sessionTimeoutMinutes: number;
  passwordExpiryDays: number;
  hmrcDeadlineAlertsEnabled: boolean;
  shiftUnderstaffingAlerts: boolean;
  alertThresholdPercent: number;
  payrollRetentionYears: number;
  auditLogRetentionMonths: number;
  defaultEmployeeRole: string;
  crossCenterDataAccess: boolean;
  gdprAnonymisationEnabled: boolean;
  rightToWorkExpiryAlerts: boolean;
  policyAudit: Record<string, { modifiedAt: string; modifiedBy: string }>;
}

export interface HmrcTaxBand {
  id?: string;
  companyId: string;
  taxYear: string;
  bandName: string;
  lowerLimit: number;
  upperLimit: number | null;
  ratePercent: number;
}

export interface HmrcBulkUpdate {
  id?: string;
  companyId: string;
  performedBy?: string;
  updateType: string;
  affectedCount: number;
  details: Record<string, any>;
  createdAt?: string;
}

// ─── HELPERS ─────────────────────────────────────────────────

function toSnake(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    result[snakeKey] = value;
  }
  return result;
}

function toCamel(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    result[camelKey] = value;
  }
  return result;
}

// ─── TENANT CONFIG SERVICE ────────────────────────────────────

export const tenantConfigService = {

  async getCompanyId(): Promise<string | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data } = await supabase
      .from('user_profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();
    return data?.company_id || null;
  },

  // ── HMRC ──────────────────────────────────────────────────

  async getHmrcSettings(companyId: string): Promise<HmrcSettings | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('hmrc_settings')
      .select('*')
      .eq('company_id', companyId)
      .single();
    if (error || !data) return null;
    return toCamel(data) as HmrcSettings;
  },

  async upsertHmrcSettings(settings: HmrcSettings): Promise<HmrcSettings | null> {
    const supabase = createClient();
    const payload = toSnake({ ...settings, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('hmrc_settings')
      .upsert(payload, { onConflict: 'company_id' })
      .select()
      .single();
    if (error) throw error;
    return toCamel(data) as HmrcSettings;
  },

  async getTaxBands(companyId: string): Promise<HmrcTaxBand[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('hmrc_tax_bands')
      .select('*')
      .eq('company_id', companyId)
      .order('lower_limit', { ascending: true });
    if (error || !data) return [];
    return data.map(d => toCamel(d) as HmrcTaxBand);
  },

  async upsertTaxBand(band: HmrcTaxBand): Promise<HmrcTaxBand> {
    const supabase = createClient();
    const payload = toSnake({ ...band, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('hmrc_tax_bands')
      .upsert(payload)
      .select()
      .single();
    if (error) throw error;
    return toCamel(data) as HmrcTaxBand;
  },

  async deleteTaxBand(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('hmrc_tax_bands').delete().eq('id', id);
    if (error) throw error;
  },

  async getBulkUpdateHistory(companyId: string): Promise<HmrcBulkUpdate[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('hmrc_bulk_updates')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error || !data) return [];
    return data.map(d => toCamel(d) as HmrcBulkUpdate);
  },

  async createBulkUpdate(update: HmrcBulkUpdate): Promise<void> {
    const supabase = createClient();
    const payload = toSnake(update);
    const { error } = await supabase.from('hmrc_bulk_updates').insert(payload);
    if (error) throw error;
  },

  // ── COMPANY DEFAULTS ──────────────────────────────────────

  async getCompanyDefaults(companyId: string): Promise<CompanyDefaults | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('company_defaults')
      .select('*')
      .eq('company_id', companyId)
      .single();
    if (error || !data) return null;
    return toCamel(data) as CompanyDefaults;
  },

  async upsertCompanyDefaults(defaults: CompanyDefaults): Promise<CompanyDefaults | null> {
    const supabase = createClient();
    const payload = toSnake({ ...defaults, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('company_defaults')
      .upsert(payload, { onConflict: 'company_id' })
      .select()
      .single();
    if (error) throw error;
    return toCamel(data) as CompanyDefaults;
  },

  // ── SHIFT & PAYROLL RULES ─────────────────────────────────

  async getShiftPayrollRules(companyId: string): Promise<ShiftPayrollRules | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('shift_payroll_rules')
      .select('*')
      .eq('company_id', companyId)
      .single();
    if (error || !data) return null;
    return toCamel(data) as ShiftPayrollRules;
  },

  async upsertShiftPayrollRules(rules: ShiftPayrollRules): Promise<ShiftPayrollRules | null> {
    const supabase = createClient();
    const payload = toSnake({ ...rules, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('shift_payroll_rules')
      .upsert(payload, { onConflict: 'company_id' })
      .select()
      .single();
    if (error) throw error;
    return toCamel(data) as ShiftPayrollRules;
  },

  // ── LOCATIONS ─────────────────────────────────────────────

  async getLocations(companyId: string): Promise<TenantLocation[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('tenant_locations')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });
    if (error || !data) return [];
    return data.map(d => toCamel(d) as TenantLocation);
  },

  async upsertLocation(location: TenantLocation): Promise<TenantLocation> {
    const supabase = createClient();
    const payload = toSnake({ ...location, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('tenant_locations')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    if (error) throw error;
    return toCamel(data) as TenantLocation;
  },

  async deleteLocation(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('tenant_locations').delete().eq('id', id);
    if (error) throw error;
  },

  async updateGeofence(id: string, lat: number, lng: number, radius: number): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('tenant_locations')
      .update({ geofence_lat: lat, geofence_lng: lng, geofence_radius: radius, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  // ── SYSTEM POLICIES ───────────────────────────────────────

  async getSystemPolicies(companyId: string): Promise<SystemPolicies | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('system_policies')
      .select('*')
      .eq('company_id', companyId)
      .single();
    if (error || !data) return null;
    return toCamel(data) as SystemPolicies;
  },

  async upsertSystemPolicies(policies: SystemPolicies): Promise<SystemPolicies | null> {
    const supabase = createClient();
    const payload = toSnake({ ...policies, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('system_policies')
      .upsert(payload, { onConflict: 'company_id' })
      .select()
      .single();
    if (error) throw error;
    return toCamel(data) as SystemPolicies;
  },

  async updatePolicyField(
    companyId: string,
    fieldName: string,
    value: any,
    modifiedBy: string
  ): Promise<void> {
    const supabase = createClient();
    const snakeField = fieldName.replace(/([A-Z])/g, '_$1').toLowerCase();
    const auditKey = fieldName;
    const { data: existing } = await supabase
      .from('system_policies')
      .select('policy_audit')
      .eq('company_id', companyId)
      .single();
    const currentAudit = existing?.policy_audit || {};
    const newAudit = {
      ...currentAudit,
      [auditKey]: { modifiedAt: new Date().toISOString().split('T')[0], modifiedBy },
    };
    const { error } = await supabase
      .from('system_policies')
      .update({ [snakeField]: value, policy_audit: newAudit, updated_at: new Date().toISOString() })
      .eq('company_id', companyId);
    if (error) throw error;
  },
};
